import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Paperclip, Loader2, X, Image, FileText, ChevronDown, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChatMessage } from './ChatMessage';
import { BatchProcessing } from './BatchProcessing';
import api from '@/lib/api';
import { toast } from 'sonner';
import { notifyChatResponse } from '@/lib/notifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Agent versions
const AGENT_VERSIONS = [
  { id: 'v3', name: 'v3 (Aktuell)', description: '~60-240s Latenz' },
  { id: 'v1', name: 'v1 (Legacy)', description: '~240s Latenz' }
];

// Constants for file uploads
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB total

// Format duration in human-readable format
const formatDuration = (ms) => {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function ChatInterface({ conversationId, theme, onConversationUpdate, onSelectConversation, onOpenSidebar }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);
  const [files, setFiles] = useState([]); // Changed to array for multiple files
  const [isUploading, setIsUploading] = useState(false);
  const [agentVersion, setAgentVersion] = useState('v3'); // Default to v3
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const loadingTimerRef = useRef(null);

  // Track loading time for user feedback
  useEffect(() => {
    if (isLoading) {
      setLoadingTime(0);
      loadingTimerRef.current = setInterval(() => {
        setLoadingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setLoadingTime(0);
    }
    return () => {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
      }
    };
  }, [isLoading]);

  // Auto-refresh: Poll for updates when conversation is open and not loading
  useEffect(() => {
    if (!conversationId || isLoading) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/conversations/${conversationId}`);
        const newMessages = response.data.messages || [];
        
        // Only update if message count changed (new message arrived)
        if (newMessages.length !== messages.length) {
          // Check if it's a new assistant message
          const lastNewMessage = newMessages[newMessages.length - 1];
          if (lastNewMessage?.role === 'assistant' && newMessages.length > messages.length) {
            // Notify user of new response
            notifyChatResponse(response.data.title || 'Chat');
          }
          setMessages(newMessages);
        }
      } catch (error) {
        // Silently ignore polling errors
        console.debug('Polling error:', error);
      }
    }, 10000); // Poll every 10 seconds
    
    return () => clearInterval(pollInterval);
  }, [conversationId, isLoading, messages.length]);

  // Also refresh when window/tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && conversationId && !isLoading) {
        loadConversation(conversationId);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [conversationId, isLoading]);

  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
    } else {
      setMessages([]);
    }
    // Reset loading state when switching conversations
    setIsLoading(false);
    setLoadingTime(0);
    if (loadingTimerRef.current) {
      clearInterval(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const loadConversation = async (id) => {
    try {
      const response = await api.get(`/conversations/${id}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading conversation:', error);
      toast.error('Failed to load conversation');
    }
  };

  // Validate and add files
  const addFiles = useCallback((newFiles) => {
    const fileArray = Array.from(newFiles);
    
    // Check max files limit
    if (files.length + fileArray.length > MAX_FILES) {
      toast.error(`Maximal ${MAX_FILES} Dateien erlaubt`);
      return;
    }
    
    // Validate each file
    const validFiles = [];
    let totalSize = files.reduce((sum, f) => sum + f.size, 0);
    
    for (const file of fileArray) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" ist zu groß (max. 10MB)`);
        continue;
      }
      
      if (totalSize + file.size > MAX_TOTAL_SIZE) {
        toast.error(`Gesamtgröße überschreitet 20MB Limit`);
        break;
      }
      
      // Check for duplicates
      if (files.some(f => f.name === file.name && f.size === file.size)) {
        toast.error(`"${file.name}" bereits hinzugefügt`);
        continue;
      }
      
      validFiles.push(file);
      totalSize += file.size;
    }
    
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      const fileNames = validFiles.map(f => f.name).join(', ');
      toast.success(`${validFiles.length} Datei(en) hinzugefügt: ${fileNames}`);
    }
  }, [files]);

  // Handle file selection from input
  const handleFileSelect = (event) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
    // Reset input to allow selecting same file again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle paste event for images
  const handlePaste = useCallback((event) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    
    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // Generate a name for pasted images
          const timestamp = new Date().toISOString().slice(11, 19).replace(/:/g, '-');
          const extension = item.type.split('/')[1] || 'png';
          const namedFile = new File([file], `Screenshot_${timestamp}.${extension}`, { type: file.type });
          imageFiles.push(namedFile);
        }
      }
    }
    
    if (imageFiles.length > 0) {
      event.preventDefault(); // Prevent default paste behavior for images
      addFiles(imageFiles);
    }
  }, [addFiles]);

  // Remove a specific file
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload all files
  const uploadFiles = async () => {
    if (files.length === 0) return [];

    setIsUploading(true);
    const uploadedFiles = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        uploadedFiles.push({
          file_id: response.data.file_id,
          orq_file_id: response.data.orq_file_id,
          name: file.name,
          type: file.type
        });
      }
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Fehler beim Hochladen der Dateien');
      return uploadedFiles; // Return what we managed to upload
    } finally {
      setIsUploading(false);
    }
  };

  // Poll for task completion
  // v1 agent can take up to 15+ minutes, v2 up to 5 minutes
  const pollTaskStatus = async (taskId, convId, selectedAgentVersion, maxAttempts = 600) => {
    const POLL_INTERVAL = 3000; // 3 seconds
    // 600 attempts × 3s = 1800s = 30 minutes max wait time
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await api.get(`/chat/status/${taskId}`, {
          params: { conversation_id: convId, agent_version: selectedAgentVersion },
          timeout: 15000,
        });
        
        const { status, content, error, duration } = response.data;
        
        if (status === 'completed') {
          return { success: true, content, error: null, duration };
        } else if (status === 'failed' || status === 'error') {
          return { success: false, content: null, error: error || 'Task fehlgeschlagen', duration: null };
        }
        
        // Still working, wait and try again
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
        
      } catch (pollError) {
        console.error('Polling error:', pollError);
        // Continue polling on network errors
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
      }
    }
    
    return { success: false, content: null, error: 'Timeout beim Warten auf Antwort (30 min überschritten)' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!input.trim() && files.length === 0) || isLoading) return;

    // Store file info before clearing (support multiple files)
    const attachedFiles = files.length > 0 
      ? files.map(f => ({ name: f.name, type: f.type }))
      : null;
    
    // Track request start time
    const requestStartTime = Date.now();
    
    const userMessage = {
      role: 'user',
      content: input || `[${files.length} Datei(en) angehängt]`,
      timestamp: new Date().toISOString(),
      files: attachedFiles, // Changed from 'file' to 'files'
    };

    const userInput = input; // Save before clearing
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      let uploadedFiles = [];
      if (files.length > 0) {
        const fileNames = files.map(f => f.name).join(', ');
        uploadedFiles = await uploadFiles();
        setFiles([]); // Clear all files
        if (fileInputRef.current) fileInputRef.current.value = '';
        
        if (uploadedFiles.length > 0) {
          toast.success(`${uploadedFiles.length} Datei(en) hochgeladen`);
        }
      }

      // Step 1: Start the agent in background mode
      // Send array of file_ids for multiple files
      const fileIds = uploadedFiles.map(f => f.file_id);
      const startResponse = await api.post('/chat/start', {
        messages: messages,
        user_input: userInput,
        conversation_id: conversationId,
        file_ids: fileIds, // Changed from file_id to file_ids (array)
        agent_version: agentVersion, // Include selected agent version
      }, {
        timeout: 30000, // 30 seconds for start request
      });

      const { task_id, conversation_id: newConvId } = startResponse.data;
      
      // Notify about new conversation
      if (onConversationUpdate && newConvId) {
        onConversationUpdate(newConvId);
      }

      // Step 2: Poll for completion
      const result = await pollTaskStatus(task_id, newConvId, agentVersion);
      
      // Use duration from API or calculate locally as fallback
      const durationMs = Date.now() - requestStartTime;
      const durationFormatted = result.duration || formatDuration(durationMs);
      
      if (result.success && result.content) {
        const assistantMessage = {
          role: 'assistant',
          content: result.content,
          timestamp: new Date().toISOString(),
          duration: durationFormatted,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        
        // Notify user of new response (sound + browser notification if tab not focused)
        notifyChatResponse();
        
        // Also reload from server to ensure sync
        if (newConvId) {
          loadConversation(newConvId);
        }
      } else {
        throw new Error(result.error || 'Unbekannter Fehler');
      }

    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.response?.data);
      
      let errorContent = 'Sorry, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
      
      if (error.message?.includes('Timeout')) {
        errorContent = 'Die Anfrage dauert sehr lange. Der Agent arbeitet möglicherweise noch. Bitte laden Sie die Konversation später neu.';
        toast.error('Timeout - Agent arbeitet noch');
      } else if (error.response?.status === 400) {
        const detail = error.response?.data?.detail || 'Ungültige Anfrage';
        errorContent = `Anfragefehler: ${detail}`;
        toast.error(`Fehler: ${detail}`);
      } else if (error.response?.data?.detail) {
        errorContent = error.response.data.detail;
        toast.error('Fehler beim Senden der Nachricht');
      } else {
        toast.error('Fehler beim Senden der Nachricht');
      }
      
      const errorMessage = {
        role: 'assistant',
        content: errorContent,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-screen bg-background min-w-0 w-full overflow-hidden">
      {/* Header with mobile menu button */}
      <div className="backdrop-blur-md bg-background/80 border-b border-border/50 sticky top-0 z-30 px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center gap-3">
          {/* Hamburger menu for mobile */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenSidebar}
            className="md:hidden h-9 w-9"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Chat</h1>
        </div>
      </div>

      <ScrollArea className="flex-1 px-4 md:px-6">
        {messages.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8 md:py-12">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <MessageSquare className="h-7 w-7 md:h-8 md:w-8 text-primary" />
            </div>
            <h2 className="text-lg md:text-xl font-semibold mb-2">Start a conversation</h2>
            <p className="text-sm text-muted-foreground max-w-md mb-6 md:mb-8 px-4">
              Send a message or upload a file to begin chatting with the AI assistant powered by Orq.ai.
            </p>
            
            {/* Batch Processing Component */}
            <div className="w-full max-w-2xl px-2">
              <BatchProcessing 
                onBatchComplete={() => onConversationUpdate && onConversationUpdate()}
                agentVersion={agentVersion}
              />
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {messages.map((message, index) => (
              <ChatMessage key={index} message={message} theme={theme} />
            ))}
            {isLoading && (
              <div className="flex gap-3 md:gap-4 py-4 md:py-6 px-2 md:px-4">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <Loader2 className="h-5 w-5 text-primary-foreground animate-spin" />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
                    <span className="text-sm ml-2">Agent verarbeitet...</span>
                  </div>
                  {loadingTime >= 5 && (
                    <span className="text-xs text-muted-foreground">
                      {loadingTime < 30 
                        ? `Bitte warten... (${loadingTime}s)`
                        : loadingTime < 120
                        ? `Komplexe Anfrage - dauert etwas länger (${Math.floor(loadingTime / 60)}:${(loadingTime % 60).toString().padStart(2, '0')})`
                        : `Große Analyse - bei Timeout Seite neu laden (${Math.floor(loadingTime / 60)}:${(loadingTime % 60).toString().padStart(2, '0')})`
                      }
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={scrollRef} />
      </ScrollArea>

      <div className="border-t border-border bg-card px-3 md:px-6 py-3 md:py-4">
        {/* Agent Version Selector */}
        <div className="mb-2 md:mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs md:text-sm text-muted-foreground shrink-0">Agent:</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 md:h-8 gap-1 text-xs md:text-sm"
                  disabled={isLoading}
                  data-testid="agent-selector"
                >
                  <span className="truncate">{AGENT_VERSIONS.find(a => a.id === agentVersion)?.name || 'Agent'}</span>
                  <ChevronDown className="h-3 w-3 md:h-4 md:w-4 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {AGENT_VERSIONS.map((agent) => (
                  <DropdownMenuItem
                    key={agent.id}
                    onClick={() => setAgentVersion(agent.id)}
                    className={agentVersion === agent.id ? 'bg-accent' : ''}
                    data-testid={`agent-option-${agent.id}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{agent.name}</span>
                      <span className="text-xs text-muted-foreground">{agent.description}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {agentVersion === 'v3' ? '🚀' : '🔄'}
          </span>
        </div>

        {/* File preview area - shows all selected files */}
        {files.length > 0 && (
          <div className="mb-2 md:mb-3 space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{files.length} von {MAX_FILES} Dateien ausgewählt</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFiles([])}
                className="h-6 px-2 text-xs text-destructive hover:text-destructive"
              >
                Alle entfernen
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 px-3 py-2 bg-secondary rounded-md text-sm group"
                >
                  {file.type?.startsWith('image/') ? (
                    <Image className="h-4 w-4 text-blue-500 shrink-0" />
                  ) : (
                    <FileText className="h-4 w-4 text-orange-500 shrink-0" />
                  )}
                  <span className="text-secondary-foreground truncate max-w-[150px]" title={file.name}>
                    {file.name}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    ({formatFileSize(file.size)})
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="h-5 w-5 p-0 opacity-60 hover:opacity-100 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex gap-2 md:gap-3 items-end w-full">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isLoading || isUploading}
            multiple={true}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || isUploading || files.length >= MAX_FILES}
            className="shrink-0 h-10 w-10 md:h-11 md:w-11"
            data-testid="attach-file-btn"
            title={files.length >= MAX_FILES ? `Maximum ${MAX_FILES} Dateien` : 'Dateien anhängen'}
          >
            <Paperclip className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
          
          <div className="flex-1 min-w-0">
            <Textarea
              ref={textareaRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Nachricht eingeben..."
              disabled={isLoading}
              className="w-full min-h-[40px] md:min-h-[44px] max-h-[150px] md:max-h-[200px] resize-none bg-background border-input focus-visible:ring-1 py-2 md:py-3 text-sm md:text-base"
              rows={1}
            />
          </div>
          
          <Button
            data-testid="send-button"
            type="submit"
            disabled={isLoading || (!input.trim() && files.length === 0) || isUploading}
            className="shrink-0 h-10 w-10 md:h-11 md:w-11"
            size="icon"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

function MessageSquare({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
